import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { usePO } from '@/hooks/usePOs'
import { useProductSearch } from '@/hooks/useProducts'
import { useBackorderPOItem } from '@/hooks/useBackorders'
import {
  useAddPONote,
  useApproveDirectPO,
  useApprovePO,
  useDeletePO,
  useRejectPO,
  useResubmitPO,
  useSubmitPO,
  useUpdatePO,
} from '@/hooks/usePOMutations'
import type { components } from '@/lib/api/generated'
import { createPOSchema, describeItemsError, type POFormValues } from '@/lib/schemas/po'
import { useUIStore } from '@/store/uiStore'
import { POWizard, type WizardStep } from './POWizard'
import { WizardFooter } from './CreatePOModal'

const EMPTY: POFormValues = {
  vendor: '',
  supplier: '',
  location_to_receive: '',
  notes: '',
  items: [],
  expected_delivery: '',
  vendor_po_ref: '',
  approval_notes: '',
}

// Session 7.5 — a PO has either a supplier (multi-vendor) or a direct
// vendor (single-vendor). Send whichever the user picked; clear the other so
// switching modes on edit overwrites the previous selection. The backend
// re-derives `purchase_orders.vendor` from items if we don't send one.
function toUpdateRequest(values: POFormValues): components['schemas']['UpdatePORequest'] {
  const supplier = (values.supplier ?? '').trim()
  const vendor = (values.vendor ?? '').trim()
  const location = (values.location_to_receive ?? '').trim()
  // Same guard as toCreateRequest — never PATCH a blank location, that
  // overwrites the stored value with empty string (UpdatePORequest treats
  // None as "leave alone" and "" as "clear", so a half-populated form
  // would silently wipe the location on save).
  if (!location) {
    throw new Error('Please pick a receive location before saving.')
  }
  return {
    supplier: supplier,
    vendor: !supplier && vendor ? vendor : '',
    location_to_receive: location,
    notes: values.notes,
    // Send expected_delivery on every PATCH so the field can be cleared by
    // saving an empty value, AND so a re-saved draft keeps the user's pick.
    expected_delivery: (values.expected_delivery ?? '').trim(),
    items: values.items.map((it) => ({
      variant_id: it.variant_id,
      product_id: it.product_id,
      product_title: it.product_title,
      variant_title: it.variant_title,
      sku: it.sku,
      barcode: it.barcode,
      vendor: it.vendor,
      retail_price: it.retail_price,
      cost_base: it.cost_base,
      qty_ordered: it.qty_ordered,
      notes: it.notes,
    })),
  }
}

// Thin shell — owns nothing but the Dialog and the uiStore read. Everything
// stateful lives in EditPOBody, which is keyed by po_id so it remounts when
// the user opens a different PO (see the comment on EditPOBody).
export function EditPOModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const activePO = useUIStore((s) => s.activePO)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const poId = activeModal === 'edit' ? activePO : null

  return (
    <Dialog open={poId !== null} onOpenChange={(o) => !o && setActiveModal(null)}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex h-[85vh] w-[85vw] max-w-none flex-col gap-0 overflow-hidden p-0 text-[15px] sm:max-w-none"
        showCloseButton={false}
      >
        {poId !== null && <EditPOBody key={poId} poId={poId} />}
      </DialogContent>
    </Dialog>
  )
}

// One instance per PO identity — the `key={poId}` above is load-bearing, not
// cosmetic. This modal is mounted permanently by the page, so before the key
// the SAME form instance (plus `step` and `confirmDelete`) was reused for
// every PO the user opened. Combined with `resetOptions.keepDirtyValues`
// below, a dirty `items` field array from the PO you'd just been editing
// survived into the next PO you opened: the header, left rail and the other
// scalar fields re-synced, but the line items and totals were still the
// previous PO's. Remounting is what makes each PO start from a clean form —
// don't hoist the form, the step, or the mutations back into the shell.
function EditPOBody({ poId }: { poId: string }) {
  const setActiveModal = useUIStore((s) => s.setActiveModal)

  const { data: po, isLoading } = usePO(poId)

  // Live stock for the PO's recipient, used to backfill on_hand / incoming on
  // the loaded line items. Saved POs come back without these (they're
  // display-only form fields, never persisted), so without this the Items step
  // shows a "—" for stock. Same data source the product picker uses, so the
  // numbers stay consistent. Degrades to "—" for any variant not in scope.
  const stockScope = po?.supplier
    ? { supplier: po.supplier, vendor: null }
    : { supplier: null, vendor: po?.vendor ?? null }
  const { data: stockProducts = [] } = useProductSearch('', stockScope)
  const stockByVariant = useMemo(() => {
    const m = new Map<number, (typeof stockProducts)[number]>()
    for (const p of stockProducts) m.set(Number(p.variant_id ?? 0), p)
    return m
  }, [stockProducts])

  const [step, setStep] = useState<WizardStep>(1)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Map the loaded PO into form values. Fed to useForm's `values` prop below
  // so RHF re-syncs the form the moment the async fetch resolves — this is
  // what fixes "location is blank the first time I open the PO, but there on
  // the second open". The old approach reset the form inside a useEffect that
  // ran a tick after the fields had already mounted with EMPTY, so the Select
  // captured the empty value on first paint and only looked right once the PO
  // was cached (second open). `values` makes RHF own the sync timing.
  const formValues = useMemo<POFormValues>(() => {
    if (!po) return EMPTY
    return {
      vendor: po.vendor ?? '',
      supplier: po.supplier ?? '',
      location_to_receive: po.location_to_receive ?? '',
      notes: po.notes ?? '',
      items: (po.items ?? []).map((it) => {
        const stock = stockByVariant.get(Number(it.variant_id))
        return {
          variant_id: it.variant_id,
          product_id: it.product_id,
          product_title: it.product_title,
          variant_title: it.variant_title,
          sku: it.sku,
          barcode: it.barcode,
          // Backfill per-item vendor for legacy POs (Session 7.5): if the line
          // item row doesn't carry one (pre-migration POs), fall back to the
          // PO's top-level vendor so the Zod required-vendor check doesn't trip
          // on edit of an old draft.
          vendor: it.vendor || po.vendor || '',
          retail_price: it.retail_price,
          cost_base: it.cost_base,
          qty_ordered: it.qty_ordered,
          // Display-only — surfaces the Received column in the Items step for
          // ordered/archived POs. toUpdateRequest omits it, so saving never
          // sends it back.
          qty_received: it.qty_received,
          // Display-only date-received (most recent receipt) shown under the
          // Received qty. Also omitted by toUpdateRequest.
          last_received_at: it.last_received_at,
          notes: it.notes ?? '',
          // Display-only stock context, backfilled from live inventory so the
          // Items step shows real on-hand/incoming instead of a dash.
          on_hand: stock?.on_hand ?? undefined,
          incoming: stock?.incoming ?? undefined,
          rec_qty: stock?.recommended_qty ?? undefined,
          return_rate: stock?.return_rate ?? undefined,
        }
      }),
      expected_delivery: po.expected_delivery_date ?? '',
      vendor_po_ref: '',
      // Always starts BLANK — the stored approval_notes thread displays
      // read-only in the wizard (approvalNotesThread below) and this field is
      // only the fresh "add a note" input. Prefilling it with the thread
      // would re-send the whole history on the next submit/approve and
      // double-append it server-side.
      approval_notes: '',
    }
  }, [po, stockByVariant])

  const form = useForm<POFormValues>({
    resolver: zodResolver(createPOSchema),
    defaultValues: EMPTY,
    // Keep the form in sync with the async-loaded PO. keepDirtyValues so an
    // in-flight edit isn't clobbered if the PO query happens to refetch mid-
    // session (e.g. window refocus); a fresh PO load still applies because
    // its fields aren't dirty yet. Safe only because this component is
    // per-PO — see the keyed-remount note above.
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  })

  const updateMutation = useUpdatePO()
  const backorderItemMutation = useBackorderPOItem()
  const submitMutation = useSubmitPO()
  const deleteMutation = useDeletePO()
  const approveMutation = useApprovePO()
  const approveDirectMutation = useApproveDirectPO()
  const rejectMutation = useRejectPO()
  const resubmitMutation = useResubmitPO()
  const addNoteMutation = useAddPONote()
  const busy =
    updateMutation.isPending ||
    backorderItemMutation.isPending ||
    submitMutation.isPending ||
    deleteMutation.isPending ||
    approveMutation.isPending ||
    approveDirectMutation.isPending ||
    rejectMutation.isPending ||
    resubmitMutation.isPending

  async function onApprove() {
    try {
      await approveMutation.mutateAsync({
        po_id: poId,
        notes: (form.getValues('approval_notes') ?? '').trim() || undefined,
      })
      toast.success(`Approved PO ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve PO')
    }
  }

  // Post a standalone conversation note (any status, no status change). The
  // Review step's Post note button calls this with the textarea's current
  // text; on success the box is cleared and the thread display refreshes via
  // the ['po', poId] invalidation. The modal stays open — noting isn't a
  // "finish and leave" action like submit/approve.
  async function onPostNote(text: string) {
    if (!poId) return
    try {
      await addNoteMutation.mutateAsync({ po_id: poId, notes: text })
      toast.success('Note posted')
      form.setValue('approval_notes', '')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post note')
    }
  }

  async function onReject(reason: string) {
    try {
      await rejectMutation.mutateAsync({ po_id: poId, reason })
      toast.success(`Rejected PO ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject PO')
    }
  }

  // Land on the right step as soon as the PO arrives — the component mounts
  // while the fetch is still in flight, so this fires on the load, not on
  // mount. No close branch: closing unmounts this component, which is also
  // what discards any unsaved edits.
  useEffect(() => {
    if (!po) return
    // DRAFT lands on step 1 (Setup) so the buyer continues editing where
    // they left off. AWAITING_APPROVAL lands on step 3 (Review) since
    // that's where the Approve / Reject panel lives — the approver sees
    // totals + checks + decision buttons in one view. Everything else
    // lands on step 2 (Items) since that's the most-asked surface for
    // an already-approved / ordered PO.
    setStep(
      po.status === 'DRAFT' || po.status === 'REJECTED'
        ? 1
        : po.status === 'AWAITING_APPROVAL'
          ? 3
          : 2
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po?.po_id, po?.status])

  function closeModal() {
    setActiveModal(null)
  }

  async function goNext() {
    if (step === 1) {
      const ok = await form.trigger(['vendor', 'supplier', 'location_to_receive'])
      if (ok) setStep(2)
      return
    }
    if (step === 2) {
      const ok = await form.trigger('items')
      if (ok) {
        setStep(3)
      } else {
        const msg = describeItemsError(form.formState.errors.items, form.getValues('items'))
        toast.error(msg ?? 'Some line items are missing required data.')
      }
    }
  }

  function goBack() {
    if (step === 3) setStep(2)
    else if (step === 2) setStep(1)
  }

  async function onSave() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    try {
      await updateMutation.mutateAsync({
        po_id: poId,
        body: toUpdateRequest(form.getValues()),
      })
      toast.success(`Updated PO ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update PO')
    }
  }

  async function onSubmitForApproval() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    try {
      await updateMutation.mutateAsync({
        po_id: poId,
        body: toUpdateRequest(form.getValues()),
      })
      await submitMutation.mutateAsync({
        po_id: poId,
        notes: (form.getValues('approval_notes') ?? '').trim() || undefined,
      })
      toast.success(`Submitted for approval: ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit PO')
    }
  }

  // Resubmit a REJECTED PO in place: persist edits, then transition straight
  // back to AWAITING_APPROVAL (re-notifies the approval queue). The buyer
  // never has to round-trip through DRAFT.
  async function onResubmit() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    try {
      await updateMutation.mutateAsync({
        po_id: poId,
        body: toUpdateRequest(form.getValues()),
      })
      await resubmitMutation.mutateAsync({
        po_id: poId,
        notes: (form.getValues('approval_notes') ?? '').trim() || undefined,
      })
      toast.success(`Resubmitted for approval: ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resubmit PO')
    }
  }

  // Skip-approval fast path for an existing DRAFT: persist any edits, then
  // approve directly (DRAFT → APPROVED) without the AWAITING_APPROVAL round-trip.
  async function onApproveNow() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    try {
      await updateMutation.mutateAsync({
        po_id: poId,
        body: toUpdateRequest(form.getValues()),
      })
      await approveDirectMutation.mutateAsync({
        po_id: poId,
        notes: (form.getValues('approval_notes') ?? '').trim() || undefined,
      })
      toast.success(`Approved PO ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve PO')
    }
  }

  async function onConfirmDelete() {
    try {
      await deleteMutation.mutateAsync(poId)
      toast.success(`Deleted PO ${po?.po_number || poId}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete PO')
    }
  }

  // Move a placed-PO line item to the backorder / OOS list. Operates on the
  // saved server item (resolved by variant_id), not unsaved form edits — the
  // backend removes the row and records the unreceived qty on the list.
  async function onBackorderItem(variantId: number) {
    if (!po) return
    const item = (po.items ?? []).find((it) => it.variant_id === variantId)
    if (!item) return
    const remaining = (item.qty_ordered ?? 0) - (item.qty_received ?? 0)
    if (
      !window.confirm(
        `Move ${item.product_title} to the backorder / OOS list and remove it from this PO? ` +
          `${remaining} unreceived unit${remaining === 1 ? '' : 's'} will be tracked.`
      )
    ) {
      return
    }
    try {
      await backorderItemMutation.mutateAsync({ poId, poItemId: item.po_item_id })
      toast.success(`Moved ${item.product_title} to backorder`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to move item to backorder')
    }
  }

  const isPlaced = po?.status === 'ORDERED' || po?.status === 'PARTIAL_ORDERED'

  return (
    <>
      <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3.5 sm:text-left">
        <div className="flex items-center gap-3">
          <DialogTitle className="text-base font-semibold">
            {po?.status === 'DRAFT' || po?.status === 'REJECTED'
              ? `Edit PO ${po?.po_number || poId || ''}`
              : `View PO ${po?.po_number || poId || ''} (${po?.status})`}
          </DialogTitle>
          <span className="text-xs text-[var(--mute)]">
            Step {step} of 3
          </span>
          <div className="flex-1" />
          {!isLoading && po && (
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              onClick={onSave}
              disabled={busy}
              data-action="save-header"
            >
              {updateMutation.isPending && !submitMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={closeModal}
            aria-label="Close"
            data-action="close"
          >
            ✕
          </Button>
        </div>
        <DialogDescription className="sr-only">
          View and edit this purchase order.
        </DialogDescription>
      </DialogHeader>

      {isLoading || !po ? (
        <div className="py-10 text-center text-base text-[var(--mute)]">
          Loading…
        </div>
      ) : (
        <Form {...form}>
          <form
            className="flex flex-1 flex-col overflow-hidden"
            onSubmit={(e) => e.preventDefault()}
            noValidate
          >
            {(po?.rejection_reason ?? '').trim() && po?.status !== 'APPROVED' && (
              <div
                role="alert"
                className="border-b border-[var(--line-warm)] bg-[var(--status-awaiting-bg)] px-5 py-2.5 text-xs text-[var(--status-awaiting-fg)]"
              >
                <span className="font-semibold">Previously rejected:</span>{' '}
                {po?.rejection_reason}
              </div>
            )}
            <POWizard
              step={step}
              disabled={busy}
              onStepClick={(n) => setStep(n)}
              poStatus={po?.status}
              approvalNotesThread={po?.approval_notes}
              onApprove={onApprove}
              onReject={onReject}
              approveBusy={approveMutation.isPending}
              rejectBusy={rejectMutation.isPending}
              onPostNote={onPostNote}
              postNoteBusy={addNoteMutation.isPending}
              onBackorderItem={isPlaced ? onBackorderItem : undefined}
            />
            <WizardFooter
              step={step}
              busy={busy}
              busyLabel={
                submitMutation.isPending ? 'Submitting…' : null
              }
              onCancel={closeModal}
              onBack={goBack}
              onNext={goNext}
              onSaveDraft={onSave}
              // Skip-approval fast path — only offered on DRAFT POs.
              onApproveNow={
                // The secondary step-3 button. DRAFT: self-approve fast path.
                // APPROVED Kentucky PO: a "Mark ordered (don't send)" escape
                // hatch shown ALONGSIDE the primary "Order with Kentucky" so
                // a KH PO can be marked ordered without transmitting to Arana.
                po?.status === 'DRAFT'
                  ? onApproveNow
                  : po?.status === 'APPROVED' && po?.is_kentucky
                    ? () => setActiveModal('markOrdered', poId)
                    : undefined
              }
              approveNowLabel={
                po?.status === 'APPROVED' && po?.is_kentucky
                  ? "Mark ordered (don't send)"
                  : undefined
              }
              onSubmitApproval={
                // Status-aware primary action. The button label is set
                // below via submitLabel. Click handlers:
                //   DRAFT              → save + submit for approval (existing)
                //   REJECTED           → save edits + resubmit (→ AWAITING_APPROVAL)
                //   AWAITING_APPROVAL  → open ApprovalModal (Approve/Reject)
                //   APPROVED (Kentucky)→ open OrderKentuckyModal (transmit)
                //   APPROVED (other)   → open MarkOrderedModal
                //   ORDERED+           → no button (hideSubmit is set)
                po?.status === 'AWAITING_APPROVAL'
                  ? () => setActiveModal('approval', poId)
                  : po?.status === 'APPROVED'
                    ? () => setActiveModal(po?.is_kentucky ? 'orderKentucky' : 'markOrdered', poId)
                    : po?.status === 'REJECTED'
                      ? onResubmit
                      : onSubmitForApproval
              }
              submitLabel={
                po?.status === 'AWAITING_APPROVAL'
                  ? 'Approve'
                  : po?.status === 'APPROVED'
                    ? (po?.is_kentucky ? 'Order with Kentucky' : 'Mark Ordered')
                    : po?.status === 'REJECTED'
                      ? 'Resubmit'
                      : 'Submit for approval'
              }
              hideSubmit={
                // AWAITING_APPROVAL is now driven by the inline Approve /
                // Reject panel in StepReview — hide the footer's primary
                // action so there's only one place to act.
                // After APPROVED the next state is ORDERED via the
                // MarkOrdered button; once ORDERED the receive flow takes
                // over via a separate route. Hide the primary action for
                // anything that doesn't have a sensible "next" transition
                // from inside this modal.
                po?.status === 'AWAITING_APPROVAL' ||
                po?.status === 'ORDERED' ||
                po?.status === 'PARTIAL_ORDERED' ||
                po?.status === 'ARCHIVED'
              }
              hideSaveDraft
              leftPrefix={
                po?.status === 'DRAFT' && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirmDelete(true)}
                    data-action="delete"
                  >
                    Delete
                  </Button>
                )
              }
            />
          </form>
        </Form>
      )}

      {confirmDelete && (
        <div
          role="alertdialog"
          aria-label="Confirm delete"
          className="absolute inset-0 z-50 flex items-center justify-center bg-background/95 p-6"
        >
          <div className="w-full max-w-sm space-y-4 rounded-md border bg-card p-4 shadow-md">
            <div className="text-base font-semibold">Delete this draft PO?</div>
            <p className="text-base text-muted-foreground">
              This will permanently remove PO {po?.po_number || poId} and all its line
              items.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onConfirmDelete}
                disabled={deleteMutation.isPending}
                data-action="confirm-delete"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
